import React, { useRef } from "react";
import { useEditor, EditorContent, FloatingMenu, Node, Extension, mergeAttributes } from "@tiptap/react";
import { Plugin, PluginKey } from "@tiptap/pm/state";
import StarterKit from "@tiptap/starter-kit";
import Underline from "@tiptap/extension-underline";
import TextAlign from "@tiptap/extension-text-align";
import Link from "@tiptap/extension-link";
import BlogImage from "./BlogImage";
import Placeholder from "@tiptap/extension-placeholder";
import Table from "@tiptap/extension-table";
import TableRow from "@tiptap/extension-table-row";
import TableHeader from "@tiptap/extension-table-header";
import TableCell from "@tiptap/extension-table-cell";
import { getDownloadURL, ref as sRef, uploadBytes } from "firebase/storage";
import FormatBoldIcon from "@mui/icons-material/FormatBold";
import FormatItalicIcon from "@mui/icons-material/FormatItalic";
import FormatUnderlinedIcon from "@mui/icons-material/FormatUnderlined";
import FormatStrikethroughIcon from "@mui/icons-material/FormatStrikethrough";
import CodeIcon from "@mui/icons-material/Code";
import DataObjectIcon from "@mui/icons-material/DataObject";
import FormatQuoteIcon from "@mui/icons-material/FormatQuote";
import HorizontalRuleIcon from "@mui/icons-material/HorizontalRule";
import FormatListBulletedIcon from "@mui/icons-material/FormatListBulleted";
import FormatListNumberedIcon from "@mui/icons-material/FormatListNumbered";
import FormatAlignLeftIcon from "@mui/icons-material/FormatAlignLeft";
import FormatAlignCenterIcon from "@mui/icons-material/FormatAlignCenter";
import FormatAlignRightIcon from "@mui/icons-material/FormatAlignRight";
import LinkIcon from "@mui/icons-material/Link";
import ImageIcon from "@mui/icons-material/Image";
import VideoLibraryIcon from "@mui/icons-material/VideoLibrary";
import TableChartIcon from "@mui/icons-material/TableChart";
import FormatClearIcon from "@mui/icons-material/FormatClear";
import UndoIcon from "@mui/icons-material/Undo";
import RedoIcon from "@mui/icons-material/Redo";

import storage from "../../Firebase/FirebaseConfig";

const Embed = Node.create({
  name: "embed",
  group: "block",
  atom: true,
  addAttributes() {
    return {
      src: { default: null },
      width: { default: null },
      height: { default: null },
      class: { default: null },
      frameborder: { default: "0" },
      allow: { default: null },
      allowfullscreen: { default: "true" },
    };
  },
  parseHTML() {
    return [{ tag: "iframe" }];
  },
  renderHTML({ HTMLAttributes }) {
    return ["iframe", mergeAttributes(HTMLAttributes)];
  },
});

const TrailingParagraph = Extension.create({
  name: "trailingParagraph",
  addProseMirrorPlugins() {
    return [
      new Plugin({
        key: new PluginKey("trailingParagraph"),
        appendTransaction: (_, __, state) => {
          if (state.doc.lastChild?.type.name === "paragraph") return null;
          return state.tr.insert(state.doc.content.size, state.schema.nodes.paragraph.create());
        },
      }),
    ];
  },
});

export async function uploadBodyImage(file) {
  const name = `${Date.now()}-${file.name.replace(/[^a-zA-Z0-9._-]/g, "-")}`;
  const storageRef = sRef(storage, `/blogs/body/${name}`);
  await uploadBytes(storageRef, file);
  return getDownloadURL(storageRef);
}

function insertImageFiles(view, files, pos) {
  const images = files.filter((file) => file.type.startsWith("image/"));
  if (images.length === 0) return false;
  images.forEach(async (file) => {
    try {
      const src = await uploadBodyImage(file);
      const node = view.state.schema.nodes.image.create({ src, alt: "" });
      const tr = pos == null ? view.state.tr.replaceSelectionWith(node) : view.state.tr.insert(pos, node);
      view.dispatch(tr);
    } catch (err) {
      console.error("Body image upload failed:", err);
      window.alert("That image could not be uploaded. Please try again.");
    }
  });
  return true;
}

function pickImage(editor) {
  const input = document.createElement("input");
  input.type = "file";
  input.accept = "image/*";
  input.multiple = true;
  input.onchange = () => insertImageFiles(editor.view, Array.from(input.files || []));
  input.click();
}

function toEmbedUrl(url) {
  const youtube = url.match(/(?:youtube\.com\/(?:watch\?v=|shorts\/|embed\/)|youtu\.be\/)([\w-]{11})/);
  if (youtube) return `https://www.youtube.com/embed/${youtube[1]}`;
  const vimeo = url.match(/vimeo\.com\/(\d+)/);
  if (vimeo) return `https://player.vimeo.com/video/${vimeo[1]}`;
  return url;
}

function insertVideo(editor) {
  const url = window.prompt("Video URL (YouTube, Vimeo or an embed link)");
  if (url) editor.chain().focus().insertContent({ type: "embed", attrs: { src: toEmbedUrl(url.trim()) } }).run();
}

function editLink(editor) {
  const previous = editor.getAttributes("link").href || "";
  const url = window.prompt("Link URL (leave empty to remove the link)", previous);
  if (url === null) return;
  if (url.trim() === "") editor.chain().focus().extendMarkRange("link").unsetLink().run();
  else editor.chain().focus().extendMarkRange("link").setLink({ href: url.trim() }).run();
}

const insertTable = (editor) => editor.chain().focus().insertTable({ rows: 3, cols: 3, withHeaderRow: true }).run();

const looksLikeHtml = (text) =>
  /^\s*</.test(text) && /<\/(p|h[1-6]|ul|ol|li|table|blockquote|pre|div|section|figure)>/i.test(text);

export function useBlogEditor(content, onChange) {
  const editorRef = useRef(null);
  const editor = useEditor({
    extensions: [
      StarterKit.configure({ heading: { levels: [2, 3] } }),
      Underline,
      TextAlign.configure({ types: ["heading", "paragraph"] }),
      Link.configure({ openOnClick: false }),
      BlogImage,
      Embed,
      Table.configure({ resizable: false }),
      TableRow,
      TableHeader,
      TableCell,
      Placeholder.configure({ placeholder: "Write something awesome..." }),
      TrailingParagraph,
    ],
    content,
    onUpdate: ({ editor }) => onChange(editor.getHTML().replace(/(<p><\/p>)+$/, "") || "<p></p>"),
    editorProps: {
      handlePaste: (view, event) => {
        if (insertImageFiles(view, Array.from(event.clipboardData?.files || []))) return true;
        const text = event.clipboardData?.getData("text/plain") || "";
        if (!looksLikeHtml(text) || !editorRef.current) return false;
        editorRef.current.chain().focus().insertContent(text.trim()).run();
        return true;
      },
      handleDrop: (view, event, slice, moved) => {
        if (moved) return false;
        const pos = view.posAtCoords({ left: event.clientX, top: event.clientY })?.pos;
        const handled = insertImageFiles(view, Array.from(event.dataTransfer?.files || []), pos);
        if (handled) event.preventDefault();
        return handled;
      },
    },
  });
  editorRef.current = editor;
  return editor;
}

const Button = ({ onClick, active, disabled, title, children }) => (
  <button type="button" onMouseDown={(e) => e.preventDefault()} onClick={onClick} className={active ? "is-active" : ""} disabled={disabled} title={title}>
    {children}
  </button>
);

function blockValue(editor) {
  if (editor.isActive("heading", { level: 2 })) return "h2";
  if (editor.isActive("heading", { level: 3 })) return "h3";
  return "p";
}

function setBlock(editor, value) {
  const chain = editor.chain().focus();
  if (value === "p") chain.setParagraph().run();
  else chain.setHeading({ level: value === "h2" ? 2 : 3 }).run();
}

export const EditorToolbar = ({ editor, editorToolOpen }) => {
  if (!editor) return null;
  const inTable = editor.isActive("table");
  const c = () => editor.chain().focus();

  return (
    <div className="tiptap-editor blog-tiptap-toolbar" style={editorToolOpen ? null : { display: "none" }}>
      <div className="tiptap-toolbar">
        <select value={blockValue(editor)} onChange={(e) => setBlock(editor, e.target.value)} title="Text style">
          <option value="p">Normal</option>
          <option value="h2">Heading</option>
          <option value="h3">Subheading</option>
        </select>
        <span className="divider" />
        <Button title="Bold" active={editor.isActive("bold")} onClick={() => c().toggleBold().run()}><FormatBoldIcon fontSize="small" /></Button>
        <Button title="Italic" active={editor.isActive("italic")} onClick={() => c().toggleItalic().run()}><FormatItalicIcon fontSize="small" /></Button>
        <Button title="Underline" active={editor.isActive("underline")} onClick={() => c().toggleUnderline().run()}><FormatUnderlinedIcon fontSize="small" /></Button>
        <Button title="Strikethrough" active={editor.isActive("strike")} onClick={() => c().toggleStrike().run()}><FormatStrikethroughIcon fontSize="small" /></Button>
        <Button title="Inline code" active={editor.isActive("code")} onClick={() => c().toggleCode().run()}><CodeIcon fontSize="small" /></Button>
        <Button title="Link" active={editor.isActive("link")} onClick={() => editLink(editor)}><LinkIcon fontSize="small" /></Button>
        <span className="divider" />
        <Button title="Bulleted list" active={editor.isActive("bulletList")} onClick={() => c().toggleBulletList().run()}><FormatListBulletedIcon fontSize="small" /></Button>
        <Button title="Numbered list" active={editor.isActive("orderedList")} onClick={() => c().toggleOrderedList().run()}><FormatListNumberedIcon fontSize="small" /></Button>
        <Button title="Quote" active={editor.isActive("blockquote")} onClick={() => c().toggleBlockquote().run()}><FormatQuoteIcon fontSize="small" /></Button>
        <Button title="Code block" active={editor.isActive("codeBlock")} onClick={() => c().toggleCodeBlock().run()}><DataObjectIcon fontSize="small" /></Button>
        <Button title="Divider" onClick={() => c().setHorizontalRule().run()}><HorizontalRuleIcon fontSize="small" /></Button>
        <span className="divider" />
        <Button title="Align left" active={editor.isActive({ textAlign: "left" })} onClick={() => c().setTextAlign("left").run()}><FormatAlignLeftIcon fontSize="small" /></Button>
        <Button title="Align center" active={editor.isActive({ textAlign: "center" })} onClick={() => c().setTextAlign("center").run()}><FormatAlignCenterIcon fontSize="small" /></Button>
        <Button title="Align right" active={editor.isActive({ textAlign: "right" })} onClick={() => c().setTextAlign("right").run()}><FormatAlignRightIcon fontSize="small" /></Button>
        <span className="divider" />
        <Button title="Image" onClick={() => pickImage(editor)}><ImageIcon fontSize="small" /></Button>
        <Button title="Video" onClick={() => insertVideo(editor)}><VideoLibraryIcon fontSize="small" /></Button>
        <Button title="Table" active={inTable} onClick={() => insertTable(editor)} disabled={inTable}><TableChartIcon fontSize="small" /></Button>
        {inTable && (
          <span className="table-actions">
            <Button title="Add row below" onClick={() => c().addRowAfter().run()}>+ Row</Button>
            <Button title="Add column right" onClick={() => c().addColumnAfter().run()}>+ Col</Button>
            <Button title="Delete row" onClick={() => c().deleteRow().run()}>− Row</Button>
            <Button title="Delete column" onClick={() => c().deleteColumn().run()}>− Col</Button>
            <Button title="Toggle header row" onClick={() => c().toggleHeaderRow().run()}>Header</Button>
            <Button title="Delete table" onClick={() => c().deleteTable().run()}>Delete table</Button>
          </span>
        )}
        <span className="divider" />
        <Button title="Clear formatting" onClick={() => c().unsetAllMarks().clearNodes().run()}><FormatClearIcon fontSize="small" /></Button>
        <Button title="Undo" disabled={!editor.can().undo()} onClick={() => c().undo().run()}><UndoIcon fontSize="small" /></Button>
        <Button title="Redo" disabled={!editor.can().redo()} onClick={() => c().redo().run()}><RedoIcon fontSize="small" /></Button>
      </div>
    </div>
  );
};

const blockItems = [
  { label: "Heading", run: (e) => e.chain().focus().setHeading({ level: 2 }).run() },
  { label: "Subheading", run: (e) => e.chain().focus().setHeading({ level: 3 }).run() },
  { label: "Bulleted list", run: (e) => e.chain().focus().toggleBulletList().run() },
  { label: "Numbered list", run: (e) => e.chain().focus().toggleOrderedList().run() },
  { label: "Quote", run: (e) => e.chain().focus().toggleBlockquote().run() },
  { label: "Code block", run: (e) => e.chain().focus().toggleCodeBlock().run() },
  { label: "Divider", run: (e) => e.chain().focus().setHorizontalRule().run() },
  { label: "Image", run: pickImage },
  { label: "Video", run: insertVideo },
  { label: "Table", run: insertTable },
];

export const BlogEditorContent = ({ editor }) => {
  if (!editor) return null;
  return (
    <div className="tiptap-editor blog-tiptap-content">
      <FloatingMenu editor={editor} tippyOptions={{ duration: 100, placement: "left-start" }} className="blog-insert-menu">
        {blockItems.map((item) => (
          <button key={item.label} type="button" onMouseDown={(e) => e.preventDefault()} onClick={() => item.run(editor)}>
            {item.label}
          </button>
        ))}
      </FloatingMenu>
      <EditorContent editor={editor} />
    </div>
  );
};
